/**
 * 生图记录页面
 * 展示用户所有批量任务的历史记录
 * 支持分页浏览和备注模糊搜索
 */

import { useState, useCallback, useEffect } from 'react'
import type { ReactElement } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  AlertCircle,
  Search,
  RotateCcw,
  Inbox,
  ChevronLeft,
  ChevronRight,
  X,
  History,
  Filter,
  Archive,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import type { PublicWorkflow } from '@/types/workflow'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { useAllBatchTasks } from '@/hooks/useAllBatchTasks'
import { cn } from '@/lib/utils'
import { BatchRecordCard } from '@/components/workflow'
import { getPublicWorkflowList } from '@/services/workflowService'
import { useAuthStore } from '@/stores/auth'

/**
 * 分页组件属性
 */
interface PaginationProps {
  /** 当前页码 */
  currentPage: number
  /** 总页数 */
  totalPages: number
  /** 页码变化回调 */
  onPageChange: (page: number) => void
}

/**
 * 分页组件
 */
function Pagination({
  currentPage,
  totalPages,
  onPageChange,
}: PaginationProps): ReactElement {
  if (totalPages <= 1) {
    return <></>
  }

  /**
   * 生成页码数组
   */
  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = []
    const maxVisible = 5

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      pages.push(1)

      if (currentPage > 3) {
        pages.push('...')
      }

      const start = Math.max(2, currentPage - 1)
      const end = Math.min(totalPages - 1, currentPage + 1)

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }

      if (currentPage < totalPages - 2) {
        pages.push('...')
      }

      pages.push(totalPages)
    }

    return pages
  }

  return (
    <div className="flex items-center justify-center gap-1">
      <Button
        variant="outline"
        size="icon"
        className="h-8 w-8"
        onClick={(): void => {
          onPageChange(currentPage - 1)
        }}
        disabled={currentPage === 1}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>

      {getPageNumbers().map((page, index) =>
        typeof page === 'number' ? (
          <Button
            key={page}
            variant={page === currentPage ? 'default' : 'outline'}
            size="icon"
            className="h-8 w-8"
            onClick={(): void => {
              onPageChange(page)
            }}
          >
            {page}
          </Button>
        ) : (
          <span
            key={`ellipsis-${String(index)}`}
            className="px-2 text-muted-foreground"
          >
            ...
          </span>
        )
      )}

      <Button
        variant="outline"
        size="icon"
        className="h-8 w-8"
        onClick={(): void => {
          onPageChange(currentPage + 1)
        }}
        disabled={currentPage === totalPages}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  )
}

/**
 * 加载骨架屏组件
 */
function LoadingSkeleton(): ReactElement {
  return (
    <div className="space-y-3">
      {[1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          className="animate-pulse rounded-xl border bg-card px-4 py-3"
        >
          <div className="flex items-center gap-4">
            <div className="h-10 w-10 rounded-lg bg-muted" />
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-2">
                <div className="h-4 w-32 rounded bg-muted" />
                <div className="h-4 w-16 rounded-full bg-muted" />
              </div>
              <div className="h-3 w-48 rounded bg-muted" />
            </div>
            <div className="hidden items-center gap-4 sm:flex">
              <div className="h-4 w-20 rounded bg-muted" />
              <div className="h-4 w-16 rounded bg-muted" />
            </div>
            <div className="flex gap-1">
              <div className="h-8 w-16 rounded bg-muted" />
              <div className="h-8 w-16 rounded bg-muted" />
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * 空状态组件
 */
function EmptyState(): ReactElement {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="flex flex-col items-center justify-center rounded-2xl border bg-card py-16"
    >
      <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-muted">
        <Inbox className="h-10 w-10 text-muted-foreground/50" />
      </div>
      <p className="text-lg font-medium text-muted-foreground">暂无生图记录</p>
      <p className="mt-1 text-sm text-muted-foreground/70">
        去发起新任务，开始你的创作之旅
      </p>
      <Button asChild className="mt-6">
        <Link to="/workflows">去发起新任务</Link>
      </Button>
    </motion.div>
  )
}

/**
 * 搜索无结果组件
 */
interface NoResultsProps {
  searchTerm: string
  onClear: () => void
}

function NoResults({ searchTerm, onClear }: NoResultsProps): ReactElement {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="flex flex-col items-center justify-center rounded-2xl border bg-card py-16"
    >
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
        <Search className="h-8 w-8 text-muted-foreground/50" />
      </div>
      <p className="text-lg font-medium text-muted-foreground">
        未找到匹配「{searchTerm}」的记录
      </p>
      <p className="mt-1 text-sm text-muted-foreground/70">
        试试其他关键词，或清空搜索条件
      </p>
      <Button variant="outline" className="mt-6" onClick={onClear}>
        清空搜索
      </Button>
    </motion.div>
  )
}

/**
 * 生图记录页面组件
 * @returns 页面 ReactElement
 */
export function GenerationRecordsPage(): ReactElement {
  // 搜索输入框状态（用于防抖）
  const [searchInput, setSearchInput] = useState('')
  // 工作流列表
  const [workflows, setWorkflows] = useState<PublicWorkflow[]>([])
  // 用户当前算力
  const credits = useAuthStore((state) => state.credits)

  // 使用自定义 hook 获取数据
  const {
    items,
    isLoading,
    error,
    total,
    page,
    totalPages,
    setPage,
    setRemarkSearch,
    remarkSearch,
    setWorkflowId,
    workflowId,
    refresh,
  } = useAllBatchTasks(10)

  /**
   * 获取工作流列表
   */
  useEffect(() => {
    void getPublicWorkflowList({ pageSize: 100 }).then((data) => {
      setWorkflows(data.list)
    })
  }, [])

  /**
   * 防抖搜索
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      setRemarkSearch(searchInput)
    }, 300)

    return (): void => {
      clearTimeout(timer)
    }
  }, [searchInput, setRemarkSearch])

  /**
   * 清空搜索
   */
  const handleClearSearch = useCallback((): void => {
    setSearchInput('')
    setRemarkSearch('')
    setWorkflowId('')
  }, [setRemarkSearch, setWorkflowId])

  return (
    <div className="space-y-6">
      {/* 页面头部 */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <History className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold tracking-tight">生图记录</h1>
          </div>
          <p className="text-muted-foreground">
            查看你的历史任务、消耗算力与处理结果。
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button asChild variant="outline">
            <Link to="/records/downloads">
              <Archive className="h-4 w-4" />
              下载中心
            </Link>
          </Button>

          {/* 发起新任务按钮 */}
          <Button asChild variant="secondary">
            <Link to="/workflows">
              <RotateCcw className="h-4 w-4" />
              去发起新任务
            </Link>
          </Button>
        </div>
      </div>

      {/* 算力不足提示 */}
      {credits < 0 ? (
        <Alert className="border-amber-200/80 bg-amber-50/90 text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/20 dark:text-amber-100">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle className="text-amber-900 dark:text-amber-100">
            当前算力不足，暂时无法执行生图任务
          </AlertTitle>
          <AlertDescription className="text-amber-800/90 dark:text-amber-200/90">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p>
                当前账户算力已低于 0，请先购买算力后再发起新的生图任务，避免提交失败。
              </p>
              <Button
                asChild
                size="sm"
                className="w-full sm:w-auto"
              >
                <Link to="/pricing">去购买算力</Link>
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      ) : null}

      {/* 搜索筛选表单 */}
      <div className="flex flex-col gap-3 rounded-lg border bg-card p-4 sm:flex-row sm:items-center">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Filter className="h-4 w-4" />
          <span>筛选</span>
        </div>

        {/* 备注搜索 */}
        <div className="relative flex-1 sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={searchInput}
            onChange={(event): void => {
              setSearchInput(event.target.value)
            }}
            placeholder="搜索备注..."
            className={cn(
              'h-9 w-full rounded-md border border-input bg-background pl-9 pr-9 text-sm outline-none',
              'focus:border-primary focus:ring-1 focus:ring-primary transition-all'
            )}
          />
          <AnimatePresence>
            {searchInput !== '' && (
              <motion.button
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                type="button"
                onClick={(): void => {
                  setSearchInput('')
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </motion.button>
            )}
          </AnimatePresence>
        </div>

        {/* 工作流筛选 */}
        <select
          value={workflowId}
          onChange={(event): void => {
            setWorkflowId(event.target.value)
          }}
          className={cn(
            'h-9 rounded-md border border-input bg-background px-3 text-sm outline-none',
            'focus:border-primary focus:ring-1 focus:ring-primary transition-all',
            'sm:w-48'
          )}
        >
          <option value="">全部应用</option>
          {workflows.map((workflow) => (
            <option key={workflow.id} value={workflow.id}>
              {workflow.title}
            </option>
          ))}
        </select>

        {/* 清空筛选按钮 */}
        {(searchInput !== '' || workflowId !== '') && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearSearch}
            className="text-muted-foreground"
          >
            清空筛选
          </Button>
        )}
      </div>

      {/* 错误提示 */}
      <AnimatePresence>
        {error !== null && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="rounded-lg border border-red-500/20 bg-red-500/10 p-4 text-sm text-red-600 dark:text-red-400"
          >
            <p>{error.message}</p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={refresh}
            >
              重试
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 主内容区 */}
      {isLoading ? (
        <LoadingSkeleton />
      ) : items.length === 0 ? (
        remarkSearch !== '' || workflowId !== '' ? (
          <NoResults searchTerm={remarkSearch || '当前筛选条件'} onClear={handleClearSearch} />
        ) : (
          <EmptyState />
        )
      ) : (
        <>
          {/* 记录列表 */}
          <div className="space-y-3">
            {items.map((record) => (
              <BatchRecordCard
                key={record.id}
                record={record}
                workflows={workflows}
                onRefresh={refresh}
              />
            ))}
          </div>

          {/* 分页信息和控件 */}
          <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-between">
            <p className="text-sm text-muted-foreground">
              共 {total} 条记录，第 {page}/{totalPages} 页
            </p>
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </div>
        </>
      )}
    </div>
  )
}
